import { useEffect, useMemo, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';

// types
import { SelectListPosition, SelectSize } from '../select/types';
import { AutocompleteTypes } from './types';

// components
import ClickOutsideDetector from './click-outside-detector';
import IconDown from '../icons/down';
import IconClose from '../icons/close';
import IconCopy from '../icons/copy';
import IconSelected from '../icons/selected';

import OptionsList from './options-list';

// hooks
import useFilteredList from './hooks/use-filtered-list';
import useAutocomplete from './hooks/use-autocomplete';
import usePrevious from '../../hooks/use-previous';

// styles
import styles from './autocomplete.module.css';

export default function Autocomplete({
  disabled = false,
  list = [],
  onChange,
  onSearch,
  placeholder = '',
  title = '',
  value = '',
  size = SelectSize.MEDIUM,
  name = '',
  className = '',
  borderless = false,
  fullWidth = false,
  withSuffix = false,
  isLoading = false,
  error = '',
  tabIndex,
  withClearButton,
  swap,
  valueWithDescription,
  withInfiniteScroll = false,
  hasNextPage,
  onInfinateScroll,
  nextPageIsLoading,
  inputWrapperClassName = '',
  selectListPosition = SelectListPosition.BOTTOM,
  withCopySuffix,
  onClearClick,
  resetState,
}: AutocompleteTypes): React.JSX.Element {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const ulRef = useRef<HTMLUListElement | null>(null);
  const [selectedNode, setSelectedNode] = useState(0);

  const [isCopied, setIsCopied] = useState(false);

  const {
    isOptionsListVisible,
    setOptionsListVisible,
    searchQuery,
    handleSearch,
    onSelectChange,
    handleReset,
  } = useAutocomplete({
    value,
    onSearch,
    onChange,
    name,
    list,
    search: list?.find((item) => item.value.toString() === value?.toString())
      ?.name,
  });

  const prevValue = usePrevious(value);

  const prevOpen = usePrevious(isOptionsListVisible);

  const filteredList = useFilteredList({
    list,
    searchQuery,
    isOptionsListVisible,
  });

  const selectedItem = useMemo(
    () =>
      filteredList?.find((item) => item.value.toString() === value?.toString()),
    [filteredList, value],
  );

  const onCopy = (e: React.SyntheticEvent) => {
    e.stopPropagation();

    if (selectedItem) {
      setIsCopied(true);
      copy(
        withCopySuffix && withSuffix
          ? `${selectedItem?.suffix}`
          : `${selectedItem.value}`,
      );
    }
  };

  const onClear = () => {
    handleReset();
    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }

    if (onClearClick) {
      onClearClick();
    }
  };

  const onOpen = () => {
    setOptionsListVisible(true);

    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const onScroll = (e: any) => {
    const nodes = ulRef?.current?.querySelectorAll('li');

    if (nodes) {
      const firstNode = nodes[0];
      const elHeight = firstNode.clientHeight;

      const node = nodes[Math.round(e.target.scrollTop / elHeight)];

      if (node) {
        // @ts-ignore
        const currentIndex = [].indexOf.call(nodes, node);

        if (currentIndex && currentIndex !== undefined && currentIndex !== -1) {
          const prevNode = nodes[selectedNode];
          const currentNode = nodes[currentIndex];

          const button = prevNode?.querySelector('button');
          const currentButton = currentNode?.querySelector('button');
          button?.classList?.remove(styles.hovered);
          currentButton?.classList.add(styles.hovered);

          setSelectedNode(currentIndex);
        }
      }
    }

    if (withInfiniteScroll && onInfinateScroll && !nextPageIsLoading) {
      const { scrollTop } = e.target;

      if (ulRef && ulRef.current) {
        const { scrollHeight, clientHeight } = ulRef.current;

        if (hasNextPage && scrollTop >= scrollHeight - clientHeight - 20) {
          onInfinateScroll();
        }
      }
    }
  };

  const onSelectNode = ({
    node,
    selected,
  }: {
    node: HTMLLIElement;
    selected: number;
  }) => {
    if (node) {
      const button = node.querySelector('button');

      if (button) {
        button.classList.add(styles.hovered);
      }

      const elHeight = node.clientHeight;
      const scrollTop = ulRef?.current?.scrollTop;

      if (scrollTop !== undefined && ulRef?.current) {
        const ulHeight = ulRef.current.clientHeight;
        const viewport = scrollTop + ulHeight;

        const elOffset = elHeight * selected;

        if (elOffset < scrollTop || elOffset + elHeight > viewport) {
          if (selectedNode !== 1) {
            ulRef?.current.scrollTo(0, elOffset);
          } else {
            ulRef?.current.scrollTo(0, 0);
          }
        }
      }
    }
  };

  const onEnter = () => {
    filteredList.forEach((item, index) => {
      if (index === selectedNode) {
        onSelectChange({
          target: {
            name: item.name,
            value: item.value,
          },
        });
      }
    });
  };

  const onUlKeydown = (e: { code: string; preventDefault: () => void }) => {
    const nodes = ulRef?.current?.querySelectorAll('li');
    let nextNode;

    if (nodes) {
      if (e.code === 'ArrowDown' && nodes[selectedNode + 1]) {
        setSelectedNode(selectedNode + 1);
        nextNode = selectedNode + 1;
        onSelectNode({
          node: nodes[selectedNode + 1],
          selected: selectedNode + 1,
        });
      }

      if (e.code === 'ArrowUp' && selectedNode !== 0) {
        nextNode = selectedNode - 1;

        setSelectedNode(selectedNode - 1);
        onSelectNode({
          node: nodes[selectedNode - 1],
          selected: selectedNode - 1,
        });
      }

      if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        const prevLi = nodes[selectedNode];

        if (prevLi && nodes[nextNode as number]) {
          const button = prevLi.querySelector('button');

          if (button) {
            button.classList.remove(styles.hovered);
          }
        }
      }

      e.preventDefault();
    }
  };

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 1200);
    }
  }, [isCopied]);

  useEffect(() => {
    if (ulRef && ulRef.current && isOptionsListVisible) {
      const nodes = ulRef.current.querySelectorAll('li');

      if (nodes[0]) {
        const button = nodes[0].querySelector('button');

        if (button) {
          button.classList.add(styles.hovered);
        }
      }
    }
  }, [ulRef, isOptionsListVisible]);

  useEffect(() => {
    if (swap && value && prevValue && value !== prevValue) {
      const nextValueName = list?.find((item) => item.value === value)?.name;

      if (nextValueName && nextValueName !== searchQuery) {
        handleSearch({
          target: {
            value: nextValueName,
          },
        });
      }
    }
  }, [value, prevValue, list, searchQuery, swap]);

  useEffect(() => {
    if (!isOptionsListVisible && prevOpen) {
      setSelectedNode(0);

      ulRef?.current?.removeEventListener('keydown', () => {});
    }
  }, [ulRef, isOptionsListVisible, prevOpen]);

  useEffect(() => {
    if (resetState) {
      handleReset();

      if (onClearClick) {
        onClearClick();
      }
    }
  }, [resetState]);

  return (
    <ClickOutsideDetector setOptionsListVisible={setOptionsListVisible}>
      <>
        {title && <div className={styles.autocompleteTitle}>{title}</div>}
        <div
          className={`${styles.autocomplete} 
          ${disabled ? styles.disabled : ''} 
          ${size === SelectSize.SMALL ? styles.small : ''}
          ${fullWidth ? styles.fullWidth : ''}
          ${borderless ? styles.borderless : ''}
          ${className}`}
        >
          <div
            className={`${styles.autocompleteInputWrapper} 
            ${withSuffix ? styles.withSuffix : ''}
            ${isOptionsListVisible ? styles.selectInputSearchActive : ''} 
            ${
              error ? styles.selectInputSearchError : ''
            } ${inputWrapperClassName}`}
            onClick={onOpen}
            onKeyDown={() => {}}
            role="button"
            tabIndex={-1}
          >
            <input
              type="text"
              name="search"
              className={styles.selectInputSearch}
              onChange={(e) => {
                handleSearch(e);
                setSelectedNode(0);
              }}
              tabIndex={tabIndex}
              value={`${searchQuery}${
                valueWithDescription &&
                value &&
                list?.find((item) => item.value === value)?.description
                  ? ` (${
                      list?.find((item) => item.value === value)?.description
                    })`
                  : ''
              }`}
              onFocus={onOpen}
              onKeyDown={(e) => {
                if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                  onUlKeydown(e);
                }

                if (e.code === 'Enter') {
                  onEnter();
                }

                if (e.code === 'Tab') {
                  setOptionsListVisible(false);
                }
              }}
              placeholder={placeholder}
              ref={searchInputRef}
              autoComplete="off"
            />
            {(withSuffix || withClearButton) &&
              (selectedItem || (withClearButton && searchQuery)) && (
                <>
                  {withSuffix && (
                    <div className={styles.selectInputSuffix}>
                      <span>{selectedItem?.suffix}</span>
                    </div>
                  )}

                  <button
                    type="button"
                    className={`${styles.iconWrapper} ${styles.iconWrapperClear}`}
                    onClick={onClear}
                    tabIndex={-1}
                    aria-label="Close"
                  >
                    <IconClose className={styles.icon} />
                  </button>
                  {withSuffix && (
                    <button
                      type="button"
                      className={`${styles.iconWrapper} ${styles.iconWrapperCopy}`}
                      onClick={onCopy}
                      tabIndex={-1}
                    >
                      {isCopied ? (
                        <IconSelected className={styles.icon} />
                      ) : (
                        <IconCopy className={styles.icon} />
                      )}
                    </button>
                  )}
                </>
              )}
            <button
              type="button"
              className={styles.iconWrapper}
              onClick={(e) => {
                e.stopPropagation();
                setOptionsListVisible((prev) => !prev);
              }}
              disabled={disabled}
              tabIndex={-1}
              aria-label="Toggle"
            >
              <IconDown
                className={`${styles.iconArrow} ${
                  isOptionsListVisible ? styles.rotate : ''
                }`}
              />
            </button>
          </div>
          {error && <div className={styles.autocompleteError}>{error}</div>}
          {isOptionsListVisible && (
            <OptionsList
              list={filteredList}
              isLoading={isLoading}
              value={value}
              onSelectChange={(e) => onSelectChange(e)}
              ulRef={ulRef}
              onScroll={onScroll}
              nextPageIsLoading={nextPageIsLoading}
              selectListPosition={selectListPosition}
            />
          )}
        </div>
      </>
    </ClickOutsideDetector>
  );
}
